import { isApiError } from '@pa/api'
import { usePasswordChanged } from '@pa/api/me'
import { useToast } from '@pa/ui'
import { useQueryClient } from '@tanstack/react-query'
import { useRouter } from '@tanstack/react-router'
import { useId, useState, type SyntheticEvent } from 'react'
import { useAuthStore, useSession } from '../auth/AuthProvider'
import { isAuthError } from '../auth/client'
import { signInHref } from '../auth/redirect'
import { toastApiError } from '../errors'
import { meQueryKey } from '../me/me'
import { AuthBanner, AuthCard, AuthTitle } from './AuthCard'
import { PasswordField } from './PasswordField'

/** Security.md §10.2: the Firebase password policy (enforce mode), minimum 12, maximum 4096, no composition rules. */
export const PASSWORD_MIN_LENGTH = 12
export const PASSWORD_MAX_LENGTH = 4096

export const SET_PASSWORD_COPY = {
  title: 'Set your own password',
  intro: 'You signed in with a temporary password. Choose a new one to continue.',
  tooShort: `Use at least ${String(PASSWORD_MIN_LENGTH)} characters.`,
  tooWeak: `That password is too weak. Use at least ${String(PASSWORD_MIN_LENGTH)} characters.`,
  mismatch: "The passwords don't match.",
  sameAsTemporary: "Choose a password that's different from your temporary one.",
  notChanged: 'Please set your new password again',
} as const

interface FieldErrors {
  password?: string
  confirm?: string
}

/** A-04 Set a new password (UserFlows A-04, Frontend §5.2). The only screen a flagged user can reach. */
export function SetPasswordPage({ siteName }: { siteName: string }) {
  const store = useAuthStore()
  const session = useSession()
  const queryClient = useQueryClient()
  const router = useRouter()
  const toast = useToast()
  const passwordChanged = usePasswordChanged()
  const id = useId()
  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [errors, setErrors] = useState<FieldErrors>({})
  const [banner, setBanner] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  /** Bumped after a successful change: both fields are hidden again (UserFlows §3). */
  const [saved, setSaved] = useState(0)

  async function validate(): Promise<FieldErrors> {
    const next: FieldErrors = {}
    if (password.length < PASSWORD_MIN_LENGTH) next.password = SET_PASSWORD_COPY.tooShort
    else if (password.length > PASSWORD_MAX_LENGTH) next.password = SET_PASSWORD_COPY.tooWeak
    else if (await store.isLastSignInPassword(password)) next.password = SET_PASSWORD_COPY.sameAsTemporary
    if (confirm !== password) next.confirm = SET_PASSWORD_COPY.mismatch
    return next
  }

  async function submit(e: SyntheticEvent) {
    e.preventDefault()
    setBanner(null)
    const found = await validate()
    setErrors(found)
    if (found.password ?? found.confirm) return
    setBusy(true)
    try {
      try {
        await store.changePassword(password)
      } catch (err) {
        const code = isAuthError(err) ? err.code : ''
        if (code === 'auth/requires-recent-login') {
          await session.signOut(signInHref({ reason: 'reauth' }))
          return
        }
        if (code === 'auth/weak-password' || code === 'auth/password-does-not-meet-requirements') {
          setErrors({ password: SET_PASSWORD_COPY.tooWeak })
          return
        }
        toastApiError(toast, err)
        return
      }
      try {
        const me = await passwordChanged.mutateAsync()
        queryClient.setQueryData(meQueryKey(), me)
        setSaved((n) => n + 1)
      } catch (err) {
        if (isApiError(err) && err.code === 'conflict' && err.details?.reason === 'password_not_changed') {
          setBanner(SET_PASSWORD_COPY.notChanged)
          return
        }
        toastApiError(toast, err)
        return
      }
      await router.navigate({ href: '/', replace: true })
    } finally {
      setBusy(false)
    }
  }

  const describe = (field: keyof FieldErrors) => (errors[field] ? `${id}-${field}-error` : undefined)

  return (
    <AuthCard siteName={siteName}>
      <div>
        <AuthTitle>{SET_PASSWORD_COPY.title}</AuthTitle>
        <p className="muted">{SET_PASSWORD_COPY.intro}</p>
      </div>
      {banner && <AuthBanner kind="error">{banner}</AuthBanner>}
      <form onSubmit={(e) => void submit(e)} noValidate>
        <div className="field">
          <label className="label" htmlFor={`${id}-password`}>
            New password
          </label>
          <PasswordField
            id={`${id}-password`}
            autoComplete="new-password"
            resetKey={saved}
            required
            aria-required
            aria-invalid={errors.password ? true : undefined}
            aria-describedby={describe('password') ?? `${id}-password-hint`}
            value={password}
            onChange={(e) => {
              setPassword(e.target.value)
            }}
          />
          {errors.password ? (
            <span className="err-text" id={`${id}-password-error`}>
              {errors.password}
            </span>
          ) : (
            <span className="hint" id={`${id}-password-hint`}>
              At least {PASSWORD_MIN_LENGTH} characters.
            </span>
          )}
        </div>
        <div className="field">
          <label className="label" htmlFor={`${id}-confirm`}>
            Confirm new password
          </label>
          <PasswordField
            id={`${id}-confirm`}
            autoComplete="new-password"
            resetKey={saved}
            required
            aria-required
            aria-invalid={errors.confirm ? true : undefined}
            aria-describedby={describe('confirm')}
            value={confirm}
            onChange={(e) => {
              setConfirm(e.target.value)
            }}
          />
          {errors.confirm && (
            <span className="err-text" id={`${id}-confirm-error`}>
              {errors.confirm}
            </span>
          )}
        </div>
        <button type="submit" className="btn btn-primary btn-block" disabled={busy}>
          {busy ? 'Saving…' : 'Set password'}
        </button>
        <button type="button" className="btn btn-ghost btn-block" onClick={() => void session.signOut()}>
          Sign out
        </button>
      </form>
    </AuthCard>
  )
}
